<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="search"/>
          {{ $t('callLookup') }}
        </h1>
      </div>
    </div>
    <div>
      <div class="row">
        <div class="col-12">
          <div class="tile">
            <div class="form-inline">
              <client-select :model="filterFormModel" attribute="client" @change="search" :isFilter="true"
                             :place-holder="$t('selectClient')" class="mr-1 mb-1"></client-select>
              <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                            :placeholder="$t('fromOrToNumber')" @change="search"></b-form-input>
              <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="direction" :options="[
                    {value: null, text: $t('direction')},
                    {value: 'inbound', text: $t('inbound')},
                    {value: 'outbound', text: $t('outbound')},
                ]"></form-select>
              <time-range-picker :model="filterFormModel" class="mr-1 mb-1" :label="$t('time')"
                                 :default-shortcut="8"></time-range-picker>
              <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
              <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="c-sorting">
            <div class="row">
              <div class="col-lg-12 c-sorting__right">
                <div class="c-sorting__btn">
                  <export v-show="filterFormModel.client && filterFormModel.keyword" :filter="filterFormModel"
                          :total-count="itemCount"></export>
                </div>
              </div>
            </div>
          </div>
          <div class="c-paging clearfix">
            <div class="c-paging__count" v-if="itemCount && filterFormModel.client && filterFormModel.keyword">
              {{ $t('found', [itemCount]) }}
            </div>
            <div class="c-paging__right">
              <div class="c-paging__numbers">
                <b-pagination
                    v-if="itemCount > filterFormModel.pageSize && filterFormModel.client && filterFormModel.keyword"
                    class="pull-right"
                    size="sm"
                    v-model="filterFormModel.page" :total-rows="itemCount"
                    :per-page="filterFormModel.pageSize"
                    @input="load"></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12" v-if="items.length && filterFormModel.client && filterFormModel.keyword">
          <div class="c-smart-table tile">
            <div class="row c-smart-table__title">
              <div class="col-3">{{ $t('clientInfo') }}</div>
              <div class="col-3">{{ $t('callInfo') }}</div>
              <div class="col-3">{{ $t('time') }}</div>
              <div class="col-3">{{ $t('user') }}</div>
            </div>
            <div class="row" v-for="item in items" :key="item._id">
              <div class="col-3">
                <div v-if="item.client">
                  <p>
                    <b>{{ $t('companyName') }}:&nbsp;</b><span>{{ item.client.companyName }}</span>
                  </p>
                  <p>
                    <b>{{ $t('phone') }}:&nbsp;</b><span>{{ item.client.phone }}</span>
                  </p>
                  <p>
                    <b>Email:&nbsp;</b><span>{{ item.client.email }}</span>
                  </p>
                  <p>
                    <b>Domain:&nbsp;</b><span>{{ item.client.domain }}</span>
                  </p>
                </div>
                <div v-else>
                  <p class="text-secondary small">{{ $t('unknown') }}</p>
                </div>
              </div>
              <div class="col-3">
                <p>
                  <b>{{ $t('fromNumber') }}:&nbsp;</b>
                  <span>{{ item.fromNumber }}</span>
                </p>
                <p>
                  <b>{{ $t('toNumber') }}:&nbsp;</b>
                  <span>{{ item.toNumber }}</span>
                </p>
                <p v-if="item.network">
                  <b>{{ $t('network') }}:&nbsp;</b>
                  <span>{{ item.network.name }}</span>
                </p>
                <p v-if="item.gateway">
                  <b>{{ $t('gateway') }}:&nbsp;</b>
                  <span>{{ item.gateway.name }}</span>
                </p>
                <p>
                  <label v-if="item.direction === 'inbound'" class="badge badge-success">{{ $t('inbound') }}</label>
                  <label v-else class="badge badge-info">{{ $t('outbound') }}</label>
                </p>
              </div>
              <div class="col-3">
                <p>
                  <b>{{ $t('startTime') }}:&nbsp;</b><span>{{ item.startTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
                </p>
                <p v-if="item.answerTime">
                  <b>{{ $t('answerTime') }}:&nbsp;</b><span>{{ item.answerTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
                </p>
                <p v-if="item.endTime">
                  <b>{{ $t('endTime') }}:&nbsp;</b><span>{{ item.endTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
                </p>
                <p>
                  <b>{{ $t('billDuration') }}:&nbsp;</b><span>{{ item.billDuration | callDuration }}</span>
                </p>
                <app-audio v-if="item.recordingDownloaded" :id="item._id" :duration="item.duration"/>
              </div>
              <div class="col-3">
                <p v-if="item.direction === 'inbound' && item.toUser && item.toUser.name">{{ item.toUser.name }}
                  ({{ item.toUser.ext }})</p>
                <p v-else-if="item.direction === 'outbound' && item.fromUser && item.fromUser.name">
                  {{ item.fromUser.name }} ({{ item.fromUser.ext }})</p>
                <p v-else class="text-secondary small">{{ $t('unknown') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 text-center text-danger" v-if="!filterFormModel.client && !filterFormModel.keyword">
          <span>{{ $t('selectClientNote') }}</span>
        </div>
        <div class="col-12 text-center text-danger" v-else-if="!items.length">
          <span>{{ $t('notFoundSearch') }}</span>
        </div>
        <div class="col-12" v-if="itemCount && filterFormModel.client && filterFormModel.keyword">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                        v-model="filterFormModel.page" :total-rows="itemCount"
                        :per-page="filterFormModel.pageSize"
                        @input="load"></b-pagination>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import _ from 'lodash';
import List from '../../mixins/List';
import ClientSelect from "../../components/ClientSelect";
import TimeRangePicker from "../../components/TimeRangePicker";
import Export from './CallExport';
import AppAudio from "@/components/AppAudio.vue";

export default {
  components: {AppAudio, TimeRangePicker, ClientSelect, Export},
  mixins: [List],
  data() {
    return {
      service: '/lookup-call',
      autoLoad: false,
      defaultFilterFormModel: {
        client: null,
        keyword: null,
        direction: null
      },
    }
  },
  methods: {
    search: _.debounce(function () {
      this.filterFormModel.page = 1;
      this.load();
    }, 200),
    load: _.debounce(function () {
      if (this.filterFormModel.client && this.filterFormModel.keyword) {
        let loader = this.$loading.show();
        this.$service.get(this.service, {
          params: this.filterFormModel
        }).then(response => {
          this.apply(response.data);
        }).catch(error => {
          if (error.response.data.message) {
            this.$swal({
              text: error.response.data.message,
              icon: 'error'
            });
          }
        }).finally(() => {
          loader.hide();
        });
      }
    }, 200, {leading: true, trailing: false}),
  },
  i18n: {
    messages: {
      en: {
        phone: 'Phone number',
        user: 'Agent',
        startTime: 'Start time',
        answerTime: 'Answer time',
        endTime: 'End time',
        billDuration: 'Bill duration',
        notFoundSearch: 'No result found! Change your search condition and try again!',
        fromNumber: 'From number',
        toNumber: 'To number',
        gateway: 'Gateway',
        selectClientNote: "Please select a client and phone number to call out or call in!",
        callLookup: "Look up calls",
        fromOrToNumber: "Phone number for outgoing or incoming calls",
        direction: "Calling direction",
        clientInfo: "Client information",
        callInfo: "Call information"
      },
      vi: {
        phone: 'SĐT',
        user: 'Nhân viên',
        startTime: 'Gọi lúc',
        answerTime: 'Trả lời',
        endTime: 'Kết thúc',
        billDuration: 'Thời gian đàm thoại',
        notFoundSearch: 'Không tìm thấy kết quả nào! Hãy thay đổi điều kiện tìm kiếm và thử lại!',
        fromNumber: 'Gọi từ SĐT',
        toNumber: 'Gọi tới SĐT',
        gateway: 'Gateway',
        selectClientNote: "Xin hãy chọn một khách hàng và SĐT gọi đi hoặc gọi đến!",
        callLookup: "Tra cứu cuộc gọi",
        fromOrToNumber: "SĐT gọi đi hoặc gọi đến",
        direction: "Hướng gọi",
        clientInfo: "Thông tin client",
        callInfo: "Thông tin cuộc gọi"
      }
    }
  }
}
</script>